<script setup lang="ts">
import { computed, toRef } from "vue";
import { useAnalysisQuery } from "@/composables/useAnalysisQuery";
import { resolveLocalizedTitle } from "@/composables/useCardData";
import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";
import type { AnalysisCount, ChartCard, OverlapData } from "@/types";
import { POPULATION_COLOR_PALETTES } from "@/lib/chartPalettes";
import { cardCodes, useAnalysisContext, useCardPopulation } from "../analysisData";
import ResultsVennChart, { type VennRegion, type VennSet } from "../Charts/ResultsVennChart.vue";
import ResultsHeatmapChart from "../Charts/ResultsHeatmapChart.vue";
import AnalysisCardShell from "./AnalysisCardShell.vue";
import PopulationSwitch from "./PopulationSwitch.vue";

const props = defineProps({
  card: { type: Object, required: true },
  readonly: { type: Boolean, default: false },
});

const card = toRef(props, "card");
const { weighted, availableCards, displayLocale } = useAnalysisContext();
const { choices, selected, filters, color: populationColor } = useCardPopulation(card);

const questionId = computed<number | null>(() => props.card.content?.question_id || null);
const codes = computed<string[]>(() => props.card.content?.codes || []);

const body = computed(() => {
  if (!questionId.value || codes.value.length < 2) return null;
  return { question_id: questionId.value, codes: codes.value, filters: filters.value };
});

const { result, loading, state } = useAnalysisQuery<OverlapData>("overlap", body);

const count = (entry: AnalysisCount) => (weighted.value ? entry.weighted_nb : entry.nb);

const respondents = computed(() => {
  if (!result.value) return null;
  return weighted.value ? result.value.weighted_respondents : result.value.respondents;
});

const questionMeta = computed<ChartCard | null>(
  () =>
    (availableCards.value as ChartCard[]).find((c) => (c.questions || []).some((q) => q.id === questionId.value)) || null,
);

const exactRegions = computed<VennRegion[]>(() =>
  (result.value?.regions || []).map((region) => ({ codes: region.codes, nb: count(region) })),
);
const regions = computed<VennRegion[]>(() => exactRegions.value.map((region) => ({ ...region, nb: Math.round(region.nb) })));

const none = computed(() => (result.value ? Math.round(count(result.value.none)) : 0));

function inclusiveCount(setCodes: string[]): number {
  const sum = exactRegions.value
    .filter((region) => setCodes.every((code) => region.codes.includes(code)))
    .reduce((total, region) => total + region.nb, 0);
  return Math.round(sum);
}

const SET_COLORS = [
  POPULATION_COLOR_PALETTES.blue[500],
  POPULATION_COLOR_PALETTES.teal[500],
  POPULATION_COLOR_PALETTES.amber[500],
];

const resolveTitle = (title: unknown) => removeHtmlMarkdownAndLineBreak(resolveLocalizedTitle(title, displayLocale.value));

const itemTitle = computed(() => {
  const question = (questionMeta.value?.questions || []).find((q) => q.id === questionId.value);
  return question?.item_token ? resolveTitle(question.item_title) : null;
});

const sets = computed<VennSet[]>(() => {
  const labels = cardCodes(questionMeta.value, resolveTitle);
  return codes.value
    .map((code, idx) => ({
      code,
      label: labels.find((answer) => answer.code === code)?.label || code,
      total: inclusiveCount([code]),
      color: props.card.options?.choice_colors?.[code] || SET_COLORS[idx % SET_COLORS.length],
    }))
    .filter((set) => set.total > 0);
});

const isVenn = computed(() => sets.value.length >= 2 && sets.value.length <= 3);

const matrixCodes = computed(() => sets.value.map((set) => set.code));
const matrixLabels = computed(() => Object.fromEntries(sets.value.map((set) => [set.code, set.label])));
const matrixRows = computed(() =>
  sets.value.map((rowSet) => {
    const row: Record<string, unknown> = { itemLabel: rowSet.label, itemN: rowSet.total };
    sets.value.forEach((colSet) => {
      if (colSet.code === rowSet.code) return;
      const count = inclusiveCount([rowSet.code, colSet.code]);
      row[`choice_${colSet.code}`] = rowSet.total > 0 ? count / rowSet.total : 0;
      row[`count_${colSet.code}`] = count;
    });
    return row;
  }),
);

const cardState = computed(() => state(sets.value.length >= 2));

const formatPercent = (value: number | null) => (value == null ? "–" : `${Math.round(value * 100)}%`);
</script>

<template>
  <AnalysisCardShell
    :card="card"
    :readonly="readonly"
    :respondents="respondents"
    :weighted="weighted"
    respondents-hint="Respondents who answered the question, the base of every share."
    description="Who picked answer A and answer B. Two or three answers draw a proportional Venn diagram; four or more show the duplication matrix: among the respondents who picked the row, the share who also picked the column."
    :state="cardState"
    state-message="Select a multiple-choice question and at least two of its answers to see their overlap.">
    <template #subtitle>
      <p v-if="questionMeta" class="text-xs text-muted-foreground">
        {{ questionMeta.name }}<template v-if="itemTitle"> · {{ itemTitle }}</template>
      </p>
    </template>
    <template #toolbar>
      <PopulationSwitch v-if="choices.length > 1" :card="card" :populations="choices" :selected="selected" />
    </template>

    <div :class="{ 'opacity-50': loading }">
      <ResultsVennChart v-if="isVenn" :sets="sets" :regions="regions" :respondents="respondents || 0" :none="none" />

      <ResultsHeatmapChart
        v-else
        :data="matrixRows"
        :choice-codes="matrixCodes"
        :choice-labels="matrixLabels"
        :accent="card.options?.color || populationColor">
        <template #tooltip="{ row, cell }">
          <div class="flex max-w-[280px] flex-col gap-0.5 text-xs">
            <span>Among the <strong>{{ row.n }}</strong> who picked</span>
            <span class="font-semibold">{{ row.label }}</span>
            <span>
              <strong class="tabular-nums">{{ formatPercent(cell.value) }}</strong>
              <span class="tabular-nums"> ({{ cell.count }})</span> also picked
            </span>
            <span class="font-semibold">{{ cell.label }}</span>
          </div>
        </template>
      </ResultsHeatmapChart>
    </div>
  </AnalysisCardShell>
</template>
